import { ArrowDownToLine, ArrowLeftRight, Loader2, Paperclip, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import { XeroConnectionSyncStage, XeroConnectionSyncStatus } from '@ankor-io/common/types/integrations/xero/types'
import { Badge, Loading } from '@ankor-io/ui-components'

import { useRunSSE } from '@/hooks/useRunSSE'
import { useXeroConnectionsQuery, useXeroSyncJobRunQuery } from '@/queries/organization'
import { formatDate } from '@/utils/format'

import { STAGE_LABELS, STAGE_ORDER } from '../../../constants/integration'
import { BackHeader } from './integration-back-header'
import { statusBadge } from './integration-status'

// ─── Constants ──────────────────────────────────────────────────────────────

const STAGE_ICONS: Record<string, ReactNode> = {
  [XeroConnectionSyncStage.LOADING_TRANSACTIONS]: <ArrowDownToLine className="size-5" />,
  [XeroConnectionSyncStage.MAPPING]: <ArrowLeftRight className="size-5" />,
  [XeroConnectionSyncStage.SYNCING_TRANSACTIONS]: <RefreshCw className="size-5" />,
  [XeroConnectionSyncStage.SYNCING_ATTACHMENTS]: <Paperclip className="size-5" />,
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const stageStatusColor = (status: XeroConnectionSyncStatus) => {
  switch (status) {
    case XeroConnectionSyncStatus.COMPLETED:
      return {
        border: 'border-green-400 dark:border-green-600',
        bg: 'bg-green-50 dark:bg-green-950/30',
        icon: 'text-green-600 dark:text-green-400',
      }
    case XeroConnectionSyncStatus.FAILED:
      return {
        border: 'border-red-400 dark:border-red-600',
        bg: 'bg-red-50 dark:bg-red-950/30',
        icon: 'text-red-600 dark:text-red-400',
      }
    case XeroConnectionSyncStatus.IN_PROGRESS:
      return {
        border: 'border-yellow-400 dark:border-yellow-600',
        bg: 'bg-yellow-50 dark:bg-yellow-950/30',
        icon: 'text-yellow-600 dark:text-yellow-400',
      }
    default:
      return {
        border: 'border-gray-200 dark:border-gray-700',
        bg: 'bg-gray-50 dark:bg-gray-900/30',
        icon: 'text-gray-400 dark:text-gray-500',
      }
  }
}

const isStageInactive = (stageIndex: number, stages: { status: XeroConnectionSyncStatus }[]) => {
  for (let i = 0; i < stageIndex; i++) {
    if (stages[i].status === XeroConnectionSyncStatus.FAILED) return true
  }
  return false
}

// ─── Stage card ─────────────────────────────────────────────────────────────

const StageCard = ({
  stageKey,
  status,
  issue,
  inactive,
  onClick,
}: {
  stageKey: XeroConnectionSyncStage
  status: XeroConnectionSyncStatus
  issue?: string
  inactive: boolean
  onClick?: () => void
}) => {
  const colors = stageStatusColor(status)
  const label = STAGE_LABELS[stageKey] ?? stageKey
  const icon = STAGE_ICONS[stageKey]

  return (
    <div
      onClick={onClick}
      className={`flex-1 min-w-[140px] rounded-xl border ${colors.border} ${colors.bg} p-5 flex flex-col items-center text-center transition-all cursor-pointer hover:shadow-md ${inactive ? 'opacity-40' : ''}`}
    >
      <div className={`size-12 rounded-xl flex items-center justify-center ${colors.bg} ${colors.icon} mb-3`}>
        {status === XeroConnectionSyncStatus.IN_PROGRESS ? <Loader2 className="size-5 animate-spin" /> : icon}
      </div>
      <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{label}</div>
      <div className="flex gap-1 flex-wrap justify-center">
        {status === XeroConnectionSyncStatus.COMPLETED && <Badge color="green">Completed</Badge>}
        {status === XeroConnectionSyncStatus.FAILED && <Badge color="red">Failed</Badge>}
        {status === XeroConnectionSyncStatus.IN_PROGRESS && <Badge color="yellow">In progress...</Badge>}
        {status === XeroConnectionSyncStatus.NOT_STARTED && <Badge color="gray">Waiting</Badge>}
      </div>
      {issue && <div className="mt-2 text-xs text-red-500 dark:text-red-400">{issue}</div>}
    </div>
  )
}

// ─── Connector between stages ───────────────────────────────────────────────

const StageConnector = ({ status }: { status: XeroConnectionSyncStatus }) => {
  const completed = status === XeroConnectionSyncStatus.COMPLETED
  const failed = status === XeroConnectionSyncStatus.FAILED
  return (
    <div className="flex items-center w-16 shrink-0">
      <div
        className={`h-0.5 w-full ${
          completed
            ? 'bg-green-400 dark:bg-green-600'
            : failed
              ? 'bg-red-300 dark:bg-red-700 border-dashed'
              : 'bg-gray-200 dark:bg-gray-700 border-dashed'
        }`}
        style={!completed ? { borderTop: '2px dashed', background: 'none' } : undefined}
      />
    </div>
  )
}

// ─── Meta field ─────────────────────────────────────────────────────────────

const MetaField = ({ label, children }: { label: string; children: ReactNode }) => {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-white">{children}</span>
    </div>
  )
}

// ─── Main view ──────────────────────────────────────────────────────────────

export const IntegrationRunPipelineView = ({
  organizationId,
  jobId,
  runId,
  onBack,
  onSelectStage,
}: {
  organizationId: string
  jobId: string
  runId: string
  onBack: () => void
  onSelectStage?: (stage: XeroConnectionSyncStage) => void
}) => {
  const { data: connections } = useXeroConnectionsQuery(organizationId)
  const connectionId = connections?.[0]?.id
  const { data: run, isPending, error } = useXeroSyncJobRunQuery(organizationId, connectionId, jobId, runId)

  useRunSSE(organizationId, connectionId, jobId, runId, run?.status === 'IN_PROGRESS')

  const stages = run
    ? STAGE_ORDER.map((stageKey) => {
        const stageData = run.data.stages[stageKey]
        return {
          key: stageKey,
          status: stageData?.status ?? XeroConnectionSyncStatus.NOT_STARTED,
          issue: stageData?.issue,
        }
      })
    : []

  return (
    <>
      <BackHeader
        label="Xero — Pipeline"
        onBack={onBack}
      />

      {isPending && <Loading variant="spinner" />}
      {error && <div className="text-sm text-red-500">Error: {error.message}</div>}

      {run && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          {/* Run metadata header */}
          <div className="flex items-center gap-5 px-5 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 flex-wrap">
            <MetaField label="Run">
              <span className="font-mono text-xs">{run.id}</span>
            </MetaField>
            <div className="w-px h-7 bg-gray-200 dark:bg-gray-700" />
            <MetaField label="Status">{statusBadge(run.status)}</MetaField>
            <div className="w-px h-7 bg-gray-200 dark:bg-gray-700" />
            <MetaField label="Started">{formatDate(run.createdAt)}</MetaField>
            {run.completedAt && (
              <>
                <div className="w-px h-7 bg-gray-200 dark:bg-gray-700" />
                <MetaField label="Completed">{formatDate(run.completedAt)}</MetaField>
              </>
            )}
          </div>

          {/* Stage pipeline */}
          <div className="flex items-center p-8 overflow-x-auto">
            {stages.map((stage, i) => (
              <div
                key={stage.key}
                className="contents"
              >
                <StageCard
                  stageKey={stage.key}
                  status={stage.status}
                  issue={stage.issue}
                  inactive={isStageInactive(i, stages)}
                  onClick={() => onSelectStage?.(stage.key)}
                />
                {i < stages.length - 1 && <StageConnector status={stage.status} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}
